import { format, fromUnixTime, isValid } from 'date-fns';

/**
 * Supported date format presets for timestamp formatting
 */
export const DATE_FORMATS = {
  ISO_DATETIME: 'yyyy-MM-dd HH:mm:ss',
  US_DATETIME: 'MM/dd/yyyy hh:mm a',
  EU_DATETIME: 'dd/MM/yyyy hh:mm a',
  ISO_DATE: 'yyyy-MM-dd',
  US_DATE: 'MM/dd/yyyy',
  EU_DATE: 'dd/MM/yyyy',
  TIME_24H: 'HH:mm:ss',
  TIME_12H: 'hh:mm a',
} as const;

export type DateFormatType = keyof typeof DATE_FORMATS;

/**
 * Configuration options for timestamp formatting
 */
export interface TimestampFormatOptions {
  format?: DateFormatType;
  fallback?: string;
  enableCache?: boolean;
}

/**
 * Cache for formatted timestamps to improve performance
 * LRU cache implementation with size limit
 */
class TimestampCache {
  private readonly cache = new Map<string, string>();
  private readonly maxSize = 1000;

  get(key: string): string | undefined {
    const value = this.cache.get(key);
    if (value !== undefined) {
      // Move to end for LRU
      this.cache.delete(key);
      this.cache.set(key, value);
    }
    return value;
  }

  set(key: string, value: string): void {
    if (this.cache.size >= this.maxSize) {
      // Remove oldest entry
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    this.cache.set(key, value);
  }

  clear(): void {
    this.cache.clear();
  }
}

const timestampCache = new TimestampCache();

/**
 * Validates if a timestamp is valid
 */
export const isValidTimestamp = (timestamp: unknown): timestamp is number => {
  if (typeof timestamp !== 'number' || isNaN(timestamp)) {
    return false;
  }

  // Check if timestamp is in reasonable range (between 1970 and 2100)
  const MIN_TIMESTAMP = 0;
  const MAX_TIMESTAMP = 4102444800; // Year 2100

  return timestamp >= MIN_TIMESTAMP && timestamp <= MAX_TIMESTAMP;
};

/**
 * Converts a Unix timestamp to a formatted date string with comprehensive options
 *
 * @param timestamp - The Unix timestamp to convert (in seconds)
 * @param options - Formatting options
 * @returns The formatted date string or fallback value
 *
 * @example
 * ```typescript
 * // Basic usage with default format
 * formatTimestamp(1640995200) // "2022-01-01 00:00:00"
 *
 * // With custom format
 * formatTimestamp(1640995200, { format: 'EU_DATETIME' }) // "01/01/2022 12:00 AM"
 *
 * // With custom date-fns format string
 * formatTimestamp(1640995200, { format: 'EEEE, MMMM do, yyyy' }) // "Saturday, January 1st, 2022"
 *
 * // With fallback for invalid timestamps
 * formatTimestamp(null, { fallback: 'No date available' }) // "No date available"
 * ```
 */
export const formatTimestamp = (timestamp: unknown, options: TimestampFormatOptions = {}): string => {
  const { format: formatType = 'ISO_DATETIME', fallback = 'Invalid timestamp', enableCache = true } = options;

  // Validate timestamp
  if (!isValidTimestamp(timestamp)) {
    return fallback;
  }

  // Get format string
  const formatString =
    typeof formatType === 'string' && formatType in DATE_FORMATS ? DATE_FORMATS[formatType] : formatType;

  // Create cache key
  const cacheKey = enableCache ? `${timestamp}-${formatString}` : '';

  // Check cache
  if (enableCache && cacheKey) {
    const cached = timestampCache.get(cacheKey);
    if (cached) {
      return cached;
    }
  }

  try {
    // Convert Unix timestamp to Date object
    const dateObject = fromUnixTime(timestamp);

    // Validate the created date
    if (!isValid(dateObject)) {
      return fallback;
    }

    // Format the date
    const formattedDate = format(dateObject, formatString);

    // Cache the result
    if (enableCache && cacheKey) {
      timestampCache.set(cacheKey, formattedDate);
    }

    return formattedDate;
  } catch (error) {
    // Log error in development only
    if (process.env.NODE_ENV === 'development') {
      console.warn('Error formatting timestamp:', error);
    }
    return fallback;
  }
};

/**
 * Formats timestamp specifically for table display
 * Legacy compatibility wrapper for existing table components
 *
 */
export const formatTableTimestamp = (timestamp: unknown, dateTimeFormat: string = DATE_FORMATS.EU_DATETIME): string => {
  return formatTimestamp(timestamp, {
    format: dateTimeFormat as DateFormatType,
    fallback: 'Invalid timestamp',
  });
};

/**
 * Formats Unix timestamp for form display
 * Legacy compatibility wrapper for existing form components
 *
 * @deprecated Use formatTimestamp with ISO_DATETIME format instead
 */
export const formatUnixTimestamp = (timestamp: unknown): string => {
  return formatTimestamp(timestamp, {
    format: 'ISO_DATETIME',
    fallback: '',
  });
};
